import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { purchasesService } from '../services/purchasesService';

export default function SalesHistory() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSales();
  }, [user]);

  const loadSales = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      console.log('Loading sales history for user:', user.id);
      const data = await purchasesService.getUserSales(user.id);
      console.log('Sales history data:', data);
      setSales(data);
    } catch (err) {
      console.error('Error loading sales:', err);
      setError('Failed to load sales history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="py-8">
          <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Sales History</h1>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-lg loading-skeleton"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded loading-skeleton mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded loading-skeleton w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="py-8">
          <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Sales History</h1>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadSales}
              className="mt-2 text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto max-w-2xl">
      <div className="py-6">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-6">Sales History</h1>

        {sales.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💰</div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No sales yet</h2>
            <p className="text-slate-500 mb-6">
              Items you sell will appear here.
            </p>
            <a
              href="/create"
              className="btn-primary inline-flex items-center"
            >
              Create Listing
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {sales.map((sale) => (
              <div key={sale.id} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center space-x-4">
                  {/* Item Image */}
                  <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                    {sale.listing?.photos?.[0] ? (
                      <img
                        src={sale.listing.photos[0]}
                        alt={sale.listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        📦
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {sale.listing?.title || 'Unknown Item'}
                    </h3>
                    <p className="text-sm text-slate-600 mb-1">
                      Sold to: {sale.buyer?.name || 'Unknown Buyer'}
                    </p>
                    <p className="text-sm text-slate-500">
                      Sold on {formatDate(sale.purchase_date)}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-green-600">
                      ${sale.listing?.price_cents ? (sale.listing.price_cents / 100).toFixed(2) : '0.00'}
                    </div>
                    <div className="text-xs text-slate-500">CAD</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sales Summary */}
        {sales.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Sales Summary</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {sales.length}
                </div>
                <div className="text-sm text-slate-600">Total Sales</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  ${sales.reduce((total, sale) => {
                    const price = sale.listing?.price_cents ? sale.listing.price_cents / 100 : 0;
                    return total + price;
                  }, 0).toFixed(2)}
                </div>
                <div className="text-sm text-slate-600">Total Revenue</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
